import React, { useState, useEffect } from "react";
import "./css/SetupCoachPage.css"; // Custom CSS for styling
import axios from "axios";
import { useNavigate } from "react-router-dom"; // Add this line
import { v4 as uuidv4 } from "uuid";
import logo from "./KICKS-6.png"; // Make sure this is the correct path to your logo
import { useLocation } from "react-router-dom"; // Import useLocation

const API_URL =
  "https://lykns00a87.execute-api.us-east-2.amazonaws.com/default/football_app_data_crud";
const API_URL_COACH =
  "https://r4scp7cqne.execute-api.us-east-2.amazonaws.com/default/newCoachStepFunction";

// Function to sanitize the team name
const sanitizeInput = (input) => {
  // Remove any characters that are not allowed (letters, numbers, underscores, hyphens, and periods)
  return input.replace(/[^a-zA-Z0-9_.\-\s]/g, "");
};

const SetupCoachPage = () => {
  const location = useLocation(); // Use useLocation to get state
  const navigate = useNavigate(); // Add this line
  const userId = location.state?.userId; // Retrieve userId from state
  const [step, setStep] = useState(1);
  const [coachName, setCoachName] = useState("");
  const [teamName, setTeamName] = useState("");
  const [clubName, setClubName] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [teamId, setTeamId] = useState(uuidv4()); // Generate a unique ID for the team
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [isSetupComplete, setIsSetupComplete] = useState(false);
  const loadingMessages = [
    "Checking VAR, this can take a minute or two...",
    "Reviewing potential offside...",
    "Examining possible handball...",
    "Verifying red card incident...",
    "Re-checking linesman’s angle...",
    "Drawing the lins...",
    "Consulting fourth official’s notes...",
    "Studying the replay angles...",
    "Reviewing different angles...",
    "This might be a tight one,,,",
  ];
  const [loadingIndex, setLoadingIndex] = useState(0);
  useEffect(() => {
    console.log("Received userId from App component:", userId);
  }, [userId]);
  useEffect(() => {
    if (!loading) return; // Only cycle messages if we are loading

    const interval = setInterval(() => {
      setLoadingIndex((prevIndex) => (prevIndex + 1) % loadingMessages.length);
    }, 5000); // Rotate every 3 seconds, for example

    return () => clearInterval(interval);
  }, [loading, loadingMessages.length]);
  useEffect(() => {
    if (errorMessage || successMessage) {
      setLoading(false); // Stop loading when an error or success message appears
    }
  }, [errorMessage, successMessage]);

  useEffect(() => {
    console.log("Received userId from WelcomePage:", userId);
  }, [userId]);

  const handleManageTeam = async () => {
    const acceptedDate = new Date().toISOString(); // ISO format for the date

    const payload = {
      operationType: "accepted_terms", // New operation for DynamoDB
      data: {
        coach_id: userId, // Pass the coach's userId
        accepted_t_c: acceptedDate, // Terms and Conditions accepted date
        accepted_policy: acceptedDate, // Privacy Policy accepted date
      },
    };

    try {
      console.log("Sending accepted_terms payload:", payload);
      const response = await axios.post(API_URL, payload);

      if (response.status === 200) {
        console.log(
          "Successfully updated terms acceptance in DynamoDB:",
          response.data
        );
        navigate("/manage-team", { state: { userId } }); // Navigate after successful API call
      } else {
        console.error("Error updating terms acceptance:", response.statusText);
        setErrorMessage("Failed to update terms acceptance. Please try again.");
      }
    } catch (error) {
      console.error("API error in handleManageTeam:", error.message);
      setErrorMessage("An error occurred. Please try again.");
    }
  };

  const handleNextStep = () => {
    console.log(`Current step: ${step}`);
    if (step === 1 && !coachName.trim()) {
      setErrorMessage("Please enter your name.");
      console.log("Error: Coach name is required.");
      return;
    }
    if (step === 2 && !teamName.trim()) {
      setErrorMessage("Please enter your team name.");
      console.log("Error: Team name is required.");
      return;
    }
    if (step === 3 && !clubName.trim()) {
      setErrorMessage("Please enter your club name.");
      console.log("Error: Club name is required.");
      return;
    }

    console.log("All validations passed for current step.");
    setErrorMessage("");
    setStep((prevStep) => prevStep + 1);
  };

  const handleCreateTeam = async () => {
    console.log("Starting handleCreateTeam function...");
    setLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    const teamPayload = {
      operationType: "create_new_team", // Ensure this matches the correct operation type for creating a team table
      data: {
        team_name: sanitizeInput(teamName), // Ensure team name is sanitized
      },
    };

    console.log("Payload for creating team table:", teamPayload);

    try {
      const teamResponse = await axios.post(API_URL, teamPayload);
      console.log("Team table response received:", teamResponse);
      console.log("Response data:", teamResponse.data);

      if (teamResponse.status === 200 && teamResponse.data.statusCode === 200) {
        console.log(`Team table for ${teamName} created successfully.`);

        // Extract and store the team ID from the response
        let responseBody = teamResponse.data.body;
        if (typeof responseBody === "string") {
          responseBody = JSON.parse(responseBody);
        }
        const newTeamId = responseBody.team_id; // Adjust according to your response structure

        if (newTeamId) {
          setTeamId(newTeamId); // Update the state with the new team ID
          console.log(`Stored new team ID: ${newTeamId}`);
        } else {
          console.warn("No team ID returned in the response.");
        }

        setStep(3); // Move to the next step to create the club
      } else {
        const errorBody = teamResponse.data.body
          ? JSON.parse(teamResponse.data.body)
          : {};
        console.error(
          "Error creating team table:",
          errorBody.error || "Unknown error"
        );
        setErrorMessage(
          "An error occurred while creating your team table. Please try again."
        );
      }
    } catch (error) {
      console.error("Error occurred while creating the team table:", error);
      console.log(
        "Error response details:",
        error.response?.data || "No additional error data."
      );
      setErrorMessage(
        "An error occurred while creating your team table. Please try again."
      );
    } finally {
      setLoading(false);
      console.log("handleCreateTeam function completed.");
    }
  };

  const handleSubmitCoachProfile = async () => {
    console.log("Starting handleSubmitCoachProfile function...");
    setLoading(true);
    setSuccessMessage("");
    setErrorMessage("");
    setIsSetupComplete(false); // Reset at the start

    const coachProfilePayloadNewCoach = {
      operationType: "setup_new_coach", // Define the operation type
      data: {
        coach_id: userId, // Replace with the actual userId
        coach_name: coachName, // Replace with the actual coach name
        team_id: teamId, // Replace with the actual team ID
        team_name: teamName, // Replace with the actual team name
      },
    };

    try {
      console.log(
        "Payload being sent to API Gateway:",
        coachProfilePayloadNewCoach
      );

      const response = await axios.post(
        API_URL_COACH, // Your API Gateway endpoint
        coachProfilePayloadNewCoach, // Pass the payload directly
        {
          headers: {
            "Content-Type": "application/json", // Explicitly set the content type
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        console.log("Step Function started successfully:", response.data);
        setSuccessMessage("Goal Stands! Let's GO!");
        setIsSetupComplete(true); // Setup is complete on success
      } else {
        console.error(
          "Unexpected response status from API Gateway:",
          response.status,
          response.data
        );
        setErrorMessage(
          `Unexpected response status: ${response.status}. Please try again.`
        );
      }
    } catch (error) {
      // Handle Axios errors
      if (error.response) {
        // Server responded with a status code outside 2xx
        console.error(
          "API responded with an error:",
          error.response.status,
          error.response.data
        );
        setErrorMessage(
          `API error: ${error.response.status} - ${
            error.response.data.message || "Unknown error occurred."
          }`
        );
      } else if (error.request) {
        // Request was made but no response received
        console.error("No response received from API Gateway:", error.request);
        setErrorMessage(
          "No response received from the server. Please try again."
        );
      } else {
        // Something happened in setting up the request
        console.error("Error setting up the request:", error.message);
        setErrorMessage(`Request error: ${error.message}`);
      }
    }
  };

  return (
    <div className="setup-coach-container">
      <div className="logo-container">
        <img src={logo} alt="KICKS Logo" className="logo animated-logo" />
      </div>
      <h2 className="title">Set Up Your Coach Profile</h2>
      {loading && (
        <div className="loading-container">
          <div className="football-spinner"></div>
          <p className="loading-text">{loadingMessages[loadingIndex]}</p>
        </div>
      )}
      {errorMessage && <p className="error-message">{errorMessage}</p>}
      {successMessage && <p className="success-message">{successMessage}</p>}

      {isSetupComplete ? (
        <div className="form-group">
          <p className="greeting">
            Setup complete! You can now manage your team.
          </p>
          {/* Terms and Conditions Checkbox */}
          <div
            className="terms-checkbox"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "10px",
            }}
          >
            <input
              type="checkbox"
              id="termsCheckbox"
              style={{ width: "16px", height: "16px" }}
              checked={hasAcceptedTerms}
              onChange={(e) => setHasAcceptedTerms(e.target.checked)}
            />
            <label
              htmlFor="termsCheckbox"
              style={{ fontSize: "0.9rem", lineHeight: "1.2", color: "#444" }}
            >
              I have reviewed and agree to the{" "}
              <a
                href="https://www.thekicksapp.com/terms.html"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "blue", textDecoration: "underline" }}
              >
                Terms and Conditions
              </a>{" "}
              and{" "}
              <a
                href="https://www.thekicksapp.com/policy.html"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "blue", textDecoration: "underline" }}
              >
                Privacy Policy
              </a>
              .
            </label>
          </div>

          {/* Manage Team Button */}
          <button
            onClick={handleManageTeam}
            className="setup-coach-button"
            disabled={!hasAcceptedTerms} // Disable unless terms are accepted
          >
            Manage Team
          </button>
        </div>
      ) : (
        <>
          {step === 1 && (
            <div className="form-group animated-step">
              <label htmlFor="coachName">What is your name?</label>
              <input
                type="text"
                id="coachName"
                value={coachName}
                onChange={(e) => setCoachName(sanitizeInput(e.target.value))}
                placeholder="Enter your name"
              />
              <button
                onClick={() => {
                  handleNextStep();
                  if (coachName) {
                    console.log(`Hi Coach ${coachName}`);
                  }
                }}
                className="setup-coach-button"
                disabled={loading}
              >
                Next
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="form-group animated-step">
              <p className="greeting">
                Hi Coach {coachName}, what is the name of your team?
              </p>
              <input
                type="text"
                value={teamName}
                onChange={(e) => setTeamName(sanitizeInput(e.target.value))}
                placeholder="Enter your team name"
              />
              <button
                onClick={handleCreateTeam}
                className="setup-coach-button"
                disabled={loading}
              >
                Next
              </button>
            </div>
          )}

          {step === 3 && (
            <div className="form-group animated-step">
              <p className="greeting">All set! Ready to save your profile?</p>
              <button
                onClick={handleSubmitCoachProfile}
                className="setup-coach-button"
                disabled={loading}
              >
                Save Profile
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SetupCoachPage;
